import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Plus, Pencil, Trash2, RefreshCw, MessageSquareText, AlertTriangle, Eye,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  utility: "bg-indigo-50 text-indigo-700 border-indigo-200",
  authentication: "bg-amber-50 text-amber-700 border-amber-200",
  marketing: "bg-rose-50 text-rose-700 border-rose-200",
};
const STATUS_COLORS = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
};

const blank = () => ({
  name: "", category: "utility", language: "en_US", status: "draft",
  header_text: "", body_text: "", footer_text: "",
});

const countParams = (...texts) => {
  let max = 0;
  for (const t of texts) {
    if (!t) continue;
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let m;
    while ((m = re.exec(t)) !== null) max = Math.max(max, parseInt(m[1], 10));
  }
  return max;
};

export default function WaTemplatesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [syncing, setSyncing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewParams, setPreviewParams] = useState([]);
  const [previewResult, setPreviewResult] = useState(null);

  const load = () => api.get("/wa-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const startEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "", category: t.category || "utility",
      language: t.language || "en_US", status: t.status || "draft",
      header_text: t.header_text || "", body_text: t.body_text || "",
      footer_text: t.footer_text || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.body_text.trim()) return toast.error("Body is required");
    const payload = {
      ...form,
      name: form.name.trim().toLowerCase().replace(/\s+/g, "_"),
      header_text: form.header_text || null,
      footer_text: form.footer_text || null,
    };
    try {
      if (editing) {
        await api.patch(`/wa-templates/${editing.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/wa-templates", payload);
        toast.success("Template created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/wa-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/wa-templates/sync");
      if (data.synced) {
        toast.success(`Synced: ${data.created} created · ${data.updated} updated`);
      } else if (data.skipped_reason === "wa_credentials_missing") {
        toast.info("WhatsApp credentials not configured. Set WHATSAPP_WABA_ID + token in backend env to sync.");
      } else {
        toast.error(data.error || "Sync failed");
      }
      load();
    } finally { setSyncing(false); }
  };

  const openPreview = (t) => {
    setPreviewTemplate(t);
    setPreviewParams(Array(t.param_count || 0).fill(""));
    setPreviewResult(null);
    setPreviewOpen(true);
  };
  const runPreview = async () => {
    try {
      const { data } = await api.post(`/wa-templates/${previewTemplate.id}/preview`, { params: previewParams });
      setPreviewResult(data.rendered);
    } catch (e) { toast.error("Preview failed"); }
  };

  const formParamCount = useMemo(
    () => countParams(form.header_text, form.body_text, form.footer_text),
    [form.header_text, form.body_text, form.footer_text]
  );

  return (
    <div data-testid="wa-templates-manager">
      <Card className="border-slate-200 bg-emerald-50/40 border-emerald-100 mb-4">
        <CardContent className="p-4 text-xs text-emerald-900 flex items-start gap-2">
          <MessageSquareText className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>WhatsApp Templates.</strong> These are pre-approved Meta Business templates used to message customers
            <em> outside</em> the 24-hour service window. Use <code className="bg-white/60 px-1 rounded">{"{{1}}"}</code>, <code className="bg-white/60 px-1 rounded">{"{{2}}"}</code>… for dynamic values. Templates must be
            <strong> approved</strong> by Meta before they can be sent. Use <em>Sync from Meta</em> to import live status.
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">WhatsApp Templates</h3>
          <p className="text-xs text-slate-500 mt-0.5">{items.length} template(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={sync} disabled={syncing}
            data-testid="wa-templates-sync-btn"
            className="border-slate-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            Sync from Meta
          </Button>
          <Button size="sm" onClick={startCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-templates-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add template
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Language</TableHead>
                <TableHead>Params</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`wa-template-row-${t.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800 font-mono">{t.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[t.category] || "bg-slate-100 text-slate-600"}`}>
                      {t.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{t.language}</TableCell>
                  <TableCell className="text-xs text-slate-600">{t.param_count}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status] || "bg-slate-100 text-slate-600"}`}>
                      {t.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openPreview(t)} data-testid={`wa-template-preview-${t.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(t)} data-testid={`wa-template-edit-${t.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(t)} className="text-rose-600" data-testid={`wa-template-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No templates yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} WhatsApp template</DialogTitle>
            <DialogDescription className="text-xs">
              Use <code className="bg-slate-100 px-1 rounded">{"{{1}}"}</code> / <code className="bg-slate-100 px-1 rounded">{"{{2}}"}</code>… for dynamic params. The template must be approved by Meta before agents can send it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Name <span className="text-rose-500">*</span></Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. ticket_status_update"
                  className="font-mono text-sm"
                  data-testid="wa-template-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Language</Label>
                <Input
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  placeholder="en_US"
                  className="font-mono text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="wa-template-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utility">Utility</SelectItem>
                    <SelectItem value="authentication">Authentication</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="wa-template-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (local-only)</SelectItem>
                    <SelectItem value="pending">Pending Meta approval</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Header <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} placeholder="Update on your support ticket" />
            </div>
            <div className="space-y-1.5">
              <Label>Body <span className="text-rose-500">*</span></Label>
              <Textarea
                value={form.body_text}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                rows={4}
                placeholder="Hi {{1}}, your ticket {{2}} status is now {{3}}."
                data-testid="wa-template-body-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Footer <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} placeholder="Reply STOP to opt out." />
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2 pt-1">
              <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                {formParamCount} param{formParamCount === 1 ? "" : "s"} detected
              </Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-template-submit">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Preview · {previewTemplate?.name}</DialogTitle>
            <DialogDescription className="text-xs">Fill in placeholder values to see how the message will render.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {(previewTemplate?.param_count || 0) === 0 ? (
              <p className="text-xs text-slate-500">This template has no parameters.</p>
            ) : (
              Array.from({ length: previewTemplate?.param_count || 0 }).map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <Label>{`{{${i + 1}}}`}</Label>
                  <Input
                    value={previewParams[i] || ""}
                    onChange={(e) => {
                      const next = [...previewParams]; next[i] = e.target.value; setPreviewParams(next);
                    }}
                  />
                </div>
              ))
            )}
            <Button size="sm" onClick={runPreview} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wa-template-preview-run">
              Render preview
            </Button>
            {previewResult && (
              <div className="rounded-md border border-slate-200 bg-[#e7f8c7] p-3 text-sm leading-relaxed font-mono whitespace-pre-wrap" data-testid="wa-template-preview-result">
                {previewResult.header && <div className="font-bold text-slate-900 mb-1">{previewResult.header}</div>}
                <div className="text-slate-800">{previewResult.body}</div>
                {previewResult.footer && <div className="text-[10px] text-slate-500 mt-2">{previewResult.footer}</div>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
